import { useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { useVisibleItemCount } from '@/hooks/use-visible-item-count';
import { cn } from '@/lib/utils';
import { ScrollableText } from '@/modules/planner/components/assignment/text/scrollable-text';

interface IndustriesCellProps {
  industries: string[];
  isArchived: boolean;
}

export function IndustriesCell({ industries, isArchived }: IndustriesCellProps) {
  const [tooltipHovered, setTooltipHovered] = useState(false);
  const { containerRef, shadowRef, visibleCount } = useVisibleItemCount(industries.length);
  const hiddenCount = industries.length - visibleCount;

  return (
    <div ref={containerRef} className="relative min-w-0">
      {/* Shadow clone for measuring item widths — invisible and out of flow */}
      <div
        ref={shadowRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute flex gap-2"
      >
        {industries.map((industry, i) => (
          <Badge key={i} variant="secondary" className="whitespace-nowrap">
            {industry}
          </Badge>
        ))}
      </div>

      {/*
       * When overflow: items wrapper shrinks (default flex-shrink: 1) and clips content,
       * badge stays adjacent with shrink-0. Scroll disabled when badge is showing
       * because the animation would move the badge out of view.
       */}
      <ScrollableText
        className={cn(isArchived && 'opacity-50')}
        paused={tooltipHovered}
        disabled={hiddenCount > 0}
      >
        <div className="flex items-center gap-2">
          <div className={cn('flex gap-2', hiddenCount > 0 && 'min-w-0 overflow-hidden')}>
            {industries.slice(0, visibleCount).map((industry, i) => (
              <Badge key={i} variant="secondary" className="whitespace-nowrap">
                {industry}
              </Badge>
            ))}
          </div>
          {hiddenCount > 0 && (
            <div className="shrink-0">
              <SimpleTooltip
                content={<p>{industries.slice(visibleCount).join(', ')}</p>}
                contentClassName="max-w-md"
              >
                <Badge
                  variant="secondary"
                  onMouseEnter={() => setTooltipHovered(true)}
                  onMouseLeave={() => setTooltipHovered(false)}
                >
                  {`+${hiddenCount}`}
                </Badge>
              </SimpleTooltip>
            </div>
          )}
        </div>
      </ScrollableText>
    </div>
  );
}
